import { componentText } from '../utils/i18n_utils';

// A summary is "open" exactly while its slice entry exists (mirroring the
// event slice): fetching seeds the entry, closing deletes it.
export const ActionTypes = {
  FETCH_SUMMARY_REQUEST: 'summaries/FETCH_SUMMARY_REQUEST',
  FETCH_SUMMARY_SUCCESS: 'summaries/FETCH_SUMMARY_SUCCESS',
  FETCH_SUMMARY_FAILURE: 'summaries/FETCH_SUMMARY_FAILURE',
  CLOSE_SUMMARY: 'summaries/CLOSE_SUMMARY',
  CLEAR_SUMMARIES: 'summaries/CLEAR_SUMMARIES',
}

// Display order of the tiles; keys match displaySummaries/summariesUrls from Rails.
export const SUMMARY_KEYS = [ 'playerPoints', 'playerPurse', 'teamPoints', 'teamPurse' ]

export const isTeamSummary = (key) => key.startsWith('team')
export const summaryMetric = (key) => (key.endsWith('Purse') ? 'purse' : 'points')
export const isPurseSummary = (key) => summaryMetric(key) === 'purse'

const SUMMARY_I18N_KEYS = {
  playerPoints: 'player_points',
  playerPurse: 'player_purse',
  teamPoints: 'team_points',
  teamPurse: 'team_purse',
}

const SUMMARY_FALLBACKS = {
  playerPoints: 'Player Points Summary',
  playerPurse: 'Player Purse Summary',
  teamPoints: 'Team Points Summary',
  teamPurse: 'Team Purse Summary',
}

export const summaryTitle = (key) =>
  componentText(`summaries.${SUMMARY_I18N_KEYS[key]}`, SUMMARY_FALLBACKS[key])

// API rows carry metric/entity-specific keys (member_id/team_id, round_points/
// round_purse, ...); flatten them so the table renders every summary the same way.
const normalizeSummary = (key, data) => {
  const metric = summaryMetric(key)
  const rowsKey = isTeamSummary(key) ? 'teams' : 'players'
  const idKey = isTeamSummary(key) ? 'team_id' : 'member_id'

  const rows = (data[rowsKey] || []).map((row) => ({
    id: row[idKey],
    name: row.name,
    valuesByTournament: Object.fromEntries(
      (row.tournament_results || []).map((result) => [ result.tournament_id, result[metric] ]),
    ),
    roundValue: row[`round_${metric}`],
    totalValue: row[`total_${metric}`],
  }))

  // A League's summary is round-scoped (only the round's awards, so round and
  // total coincide); events sum the whole league. Absent on the purse-hidden
  // empty response, which renders nothing anyway.
  return { tournaments: data.tournaments || [], rows, roundScoped: data.round_scoped === true }
}

const controllersByKey = new Map()

export const fetchSummary = (summaryKey) => async (dispatch, getState) => {
  const { summariesUrls, roundId } = getState().leaderboardV2
  const urlTemplate = summariesUrls?.[summaryKey]
  if (!urlTemplate) { return }

  controllersByKey.get(summaryKey)?.abort()
  const controller = new AbortController()
  controllersByKey.set(summaryKey, controller)

  dispatch({ type: ActionTypes.FETCH_SUMMARY_REQUEST, payload: { summaryKey } })

  try {
    const res = await fetch(urlTemplate.replace(':round_id', roundId), { signal: controller.signal })
    if (!res.ok) { throw new Error(`HTTP ${res.status}`) }
    const data = await res.json()

    if (controller.signal.aborted) { return }
    controllersByKey.delete(summaryKey)

    // A response for a superseded round must not reopen the tile.
    if (String(getState().leaderboardV2.roundId) !== String(roundId)) { return }

    dispatch({
      type: ActionTypes.FETCH_SUMMARY_SUCCESS,
      payload: { summaryKey, data: normalizeSummary(summaryKey, data) },
    })
  } catch (err) {
    if (err.name === 'AbortError') { return }
    controllersByKey.delete(summaryKey)
    if (String(getState().leaderboardV2.roundId) !== String(roundId)) { return }
    dispatch({
      type: ActionTypes.FETCH_SUMMARY_FAILURE,
      payload: { summaryKey, error: err.message || componentText('shared.request_failed', 'Request failed') },
    })
  }
}

// Close = abort any in-flight fetch, then drop the entry; a late response
// must never resurrect a closed tile.
export const closeSummary = (summaryKey) => (dispatch) => {
  controllersByKey.get(summaryKey)?.abort()
  controllersByKey.delete(summaryKey)
  dispatch({ type: ActionTypes.CLOSE_SUMMARY, payload: { summaryKey } })
}

// The tournaments list reported hide_purse: the purse tiles leave the page
// (selectEnabledSummaryKeys), so an open purse summary closes with them
// instead of lingering as an orphaned slice entry. Returns whether one was
// open, so the caller can decide what takes its screen.
export const closePurseSummaries = () => (dispatch, getState) => {
  const openPurseKeys = SUMMARY_KEYS.filter((key) => isPurseSummary(key) && getState().summaries[key])
  openPurseKeys.forEach((key) => dispatch(closeSummary(key)))
  return openPurseKeys.length > 0
}

// Drops every summary except exceptKey (all of them when omitted), aborting
// their fetches. Used for event/summary mutual exclusion and the web ->
// mobile transition, which keeps the first open summary only when no event
// is open (an open event wins).
export const clearSummaries = (exceptKey = null) => (dispatch) => {
  controllersByKey.forEach((controller, key) => {
    if (key === exceptKey) { return }
    controller.abort()
    controllersByKey.delete(key)
  })
  dispatch({ type: ActionTypes.CLEAR_SUMMARIES, payload: { exceptKey } })
}
